import React, { useState, useCallback, useRef, useEffect } from 'react';
import { validateFile } from '../../services/validationSchemas';
import { compressImage } from '../../utils/compressImage';
import './Steps.css';
import './StepPhoto.css';

// Paso dedicado: foto tamano carne con fondo blanco. Es una sola imagen
// (sin reverso, sin OCR). Al crear el driver desde la postulacion se usa como
// foto de perfil, asi no se vuelve a pedir.
const StepPhoto = ({ formData, onUpdate, onNext, onBack }) => {
  const photo = formData.photo;
  const [localError, setLocalError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (photo && photo instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(photo);
    } else {
      setImagePreview(null);
    }
  }, [photo]);

  const handleFile = useCallback(async (selectedFile) => {
    const validation = validateFile(selectedFile);
    if (!validation.valid) {
      setLocalError(validation.error);
      return;
    }
    setLocalError(null);
    onUpdate({ photo: await compressImage(selectedFile, 1200) });
  }, [onUpdate]);

  const handleInputChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) handleFile(selectedFile);
  }, [handleFile]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) handleFile(droppedFile);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback(() => setIsDragging(false), []);

  const removeFile = useCallback(() => {
    onUpdate({ photo: null });
    setLocalError(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onUpdate]);

  return (
    <div className="step-container">
      <div className="step-header">
        <span className="step-icon">🪪</span>
        <h2 className="step-title">Tu foto tipo carné</h2>
        <p className="step-subtitle">
          Sube una foto tuya con <strong>fondo blanco</strong>. La usaremos para
          tu perfil de repartidor.
        </p>
      </div>

      {/* Guia visual de como debe verse la foto */}
      <div className="photo-guide">
        <div className="photo-guide-example">
          <div className="photo-guide-frame">
            <span className="photo-guide-face">🧑</span>
          </div>
          <span className="photo-guide-caption">Así debe verse</span>
        </div>
        <ul className="photo-guide-rules">
          <li><span>✅</span> Fondo blanco o claro</li>
          <li><span>✅</span> Rostro centrado y visible</li>
          <li><span>✅</span> Buena iluminación, sin sombras</li>
          <li><span>🚫</span> Sin gorra, gafas oscuras ni filtros</li>
        </ul>
      </div>

      {/* Subida de la foto */}
      {!photo ? (
        <div
          className={`photo-dropzone photo-dropzone--single ${isDragging ? 'dragging' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={handleInputChange}
            className="file-input-hidden"
          />
          <div className="photo-dropzone-content">
            <span className="photo-camera-icon">📷</span>
            <span className="photo-dropzone-text">Toca para tomar o subir tu foto</span>
          </div>
        </div>
      ) : (
        <div className="photo-single-preview">
          <img src={imagePreview} alt="Tu foto tipo carné" className="photo-single-img" />
          <button type="button" className="photo-remove-btn" onClick={removeFile}>
            ✕
          </button>
        </div>
      )}

      {localError && (
        <div className="photo-error">
          <span>⚠️</span> {localError}
        </div>
      )}

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          <span className="btn-icon">←</span>
          Atrás
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={!photo}
        >
          {photo ? 'Continuar' : 'Sube tu foto'}
          <span className="btn-icon">→</span>
        </button>
      </div>
    </div>
  );
};

export default StepPhoto;
